import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { CalendarDays, Clock, User, ArrowRight, Tag } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSeo, useJsonLd } from "@/lib/api";
import { Section, Reveal } from "@/components/common/Section";
import Breadcrumb from "@/components/common/Breadcrumb";
import FinalCta from "@/components/common/FinalCta";
import { getArticleBySlug, publishedArticles } from "@/data/articles";

export default function Article() {
  const { slug } = useParams();
  const { t, to, lang } = useLang();
  const article = getArticleBySlug(slug);

  useSeo({
    lang,
    path: article ? `/conseils/${article.slug}` : "/conseils",
    title: article ? `${article[lang].title} — NEKASSUR` : "NEKASSUR",
    description: article ? article[lang].excerpt : "",
  });
  useJsonLd(
    "ld-article",
    article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article[lang].title,
          datePublished: article.date,
          author: { "@type": "Person", name: article.author },
          publisher: { "@type": "Organization", name: "NEKASSUR" },
        }
      : null
  );

  if (!article || !article.published) return <Navigate to={to("/conseils")} replace />;

  const c = article[lang];
  const related = publishedArticles().filter((a) => a.slug !== article.slug && a.category === article.category).slice(0, 3);
  const fallback = related.length ? related : publishedArticles().filter((a) => a.slug !== article.slug).slice(0, 3);
  const dateStr = new Date(article.date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "long", day: "numeric" });

  return (
    <>
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: t.nav.conseils.label, path: "/conseils" }, { label: c.title }]} />
          <Reveal className="mt-8 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-nek-blue/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-nek-blue"><Tag className="h-3 w-3" /> {t.conseils.categories[article.category]}</span>
            <h1 className="nek-h1 mt-5 text-white" style={{ fontSize: "clamp(2rem,3.6vw,3rem)" }}>{c.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/60">
              <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {t.conseils.by} {article.author}</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {dateStr}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {article.readingTime} {t.conseils.readingTime}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <Section tone="white">
        <div className="mx-auto max-w-3xl">
          <div className="overflow-hidden rounded-[20px] border border-nek-border shadow-soft">
            <img src={article.image} alt={c.title} className="h-72 w-full object-cover sm:h-96" />
          </div>
          <article className="mt-10 space-y-6">
            {c.body.map((para, i) => (
              <p key={`para-${i}`} className="text-[17px] leading-[1.75] text-nek-ink">{para}</p>
            ))}
          </article>
          {article.isOffer && article.offer && (
            <div className="mt-8 rounded-xl border border-nek-coral/30 bg-nek-coral/5 p-5 text-sm">
              <p className="font-semibold text-nek-navy">{article.offer[lang]}</p>
              <p className="mt-1 text-nek-gray">{t.conseils.offerConditions}</p>
            </div>
          )}
        </div>
      </Section>

      <Section tone="light">
        <h2 className="text-2xl font-heading font-bold text-nek-navy">{t.conseils.related}</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {fallback.map((a) => (
            <Link key={a.slug} to={to(`/conseils/${a.slug}`)} className="nek-card group overflow-hidden hover:-translate-y-1 hover:shadow-lift">
              <div className="overflow-hidden"><img src={a.image} alt={a[lang].title} className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>
              <div className="p-5">
                <h3 className="text-base font-heading font-bold text-nek-navy">{a[lang].title}</h3>
                <span className="link-arrow mt-3 text-sm">{t.cta.readMore} <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <FinalCta />
    </>
  );
}
